import { useQuery } from "@tanstack/react-query";
import React, { createContext, useMemo } from "react";

import { getPortalConfig, type PortalConfig } from "@/api/portal";

export interface PortalConfigValue extends PortalConfig {
  /** Есть ли раздел в ЭТОЙ инсталляции (профиль), безотносительно прав. */
  hasPage: (page: string) => boolean;
  /** Есть ли секция настроек в ЭТОЙ инсталляции (реестр
   *  `settings_sections.py`), безотносительно прав. */
  hasSection: (key: string) => boolean;
}

export const PortalConfigContext = createContext<PortalConfigValue | null>(null);

/**
 * Конфигурация инсталляции, загруженная до отрисовки роутера.
 *
 * Блокирующая загрузка — намеренно: решения «показывать ли раздел» и «пускать
 * ли на роут» принимаются по ней, и рисовать меню «как будто всё включено», а
 * потом убирать пункты — значит один кадр показывать чужие разделы.
 *
 * Ошибку загрузки НЕ маскируем «дефолтом со всеми разделами»: портал без
 * конфигурации не знает, что он такое, и должен сказать это вслух.
 */
export function PortalConfigProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["portal-config"],
    queryFn: getPortalConfig,
    // Конфигурация меняется только вместе с деплоем инсталляции.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const value = useMemo<PortalConfigValue | null>(() => {
    if (!data || !Array.isArray(data.settings_sections)) return null;
    const names = new Set(data.pages.map((p) => p.name));
    const sections = new Set(data.settings_sections.map((s) => s.key));
    return {
      ...data,
      hasPage: (page: string) => names.has(page),
      hasSection: (key: string) => sections.has(key),
    };
  }, [data]);

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Загрузка…
      </div>
    );
  }

  if (error || !value) {
    // Ответ без `settings_sections` — это СТАРЫЙ бэкенд, а не «секций нет»:
    // нарисовать портал без настроек значило бы спрятать их до первой жалобы.
    const stale = !error && !!data && !Array.isArray(data.settings_sections);
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-xl font-semibold">Портал не отвечает</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          {stale ? (
            <>
              <code>/api/config</code> не отдал <code>settings_sections</code> —
              версия бэкенда старее фронта, обновите портал.
            </>
          ) : (
            <>
              Не удалось получить конфигурацию инсталляции (<code>/api/config</code>).
              {error instanceof Error ? ` ${error.message}` : ""}
            </>
          )}
        </p>
        <button
          type="button"
          className="rounded-md border px-4 py-2 text-sm"
          onClick={() => void refetch()}
        >
          Повторить
        </button>
      </div>
    );
  }

  return (
    <PortalConfigContext.Provider value={value}>{children}</PortalConfigContext.Provider>
  );
}
